// src/api/httpClient.js
import axios from 'axios';
import { BASE_URL } from '../config/constants';

const httpClient = axios.create({ baseURL: BASE_URL, withCredentials: true });
const refreshClient = axios.create({ baseURL: BASE_URL, withCredentials: true });

let isRefreshing = false;
let refreshSubscribers = [];
function settleRefresh(error = null) {
  refreshSubscribers.forEach(cb => cb(error));
  refreshSubscribers = [];
}
function addSubscriber(cb) { refreshSubscribers.push(cb); }

// Auth endpoints should never trigger a refresh attempt.
const AUTH_NO_REFRESH_PATHS = ['/api/auth/login', '/api/auth/signup', '/api/auth/signup/verify', '/api/auth/refresh', '/api/org/register-enterprise', '/api/org/groups/invite-link/accept-link', '/api/org/groups/invite-email/accept-invite-email'];

function pathOf(url) {
  try { return new URL(url, BASE_URL).pathname; } catch { return url || ''; }
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    if (!error.response || !originalRequest) return Promise.reject(error);

    const status = error.response.status;
    const path = pathOf(originalRequest.url);
    const isAuthEndpoint = AUTH_NO_REFRESH_PATHS.some(p => path.startsWith(p));
    const isRefreshCall = path.startsWith('/api/auth/refresh');
    const skipAuthRefresh = Boolean(originalRequest.skipAuthRefresh); // optional per-request flag

    // ✅ Do NOT refresh for auth endpoints (login/signup/refresh) or if explicitly skipped
    if (status === 401 && !isAuthEndpoint && !skipAuthRefresh && !isRefreshCall) {
      if (originalRequest._retry) return Promise.reject(error);
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addSubscriber((refreshError) => {
            if (refreshError) return reject(refreshError);
            httpClient(originalRequest).then(resolve).catch(reject);
          });
        });
      }

      isRefreshing = true;
      try {
        await refreshClient.post('/api/auth/refresh'); // sets new cookies
        isRefreshing = false;
        settleRefresh();
        return httpClient(originalRequest);
      } catch (refreshErr) {
        isRefreshing = false;
        settleRefresh(refreshErr);
        try { window.dispatchEvent(new CustomEvent('auth:expired')); } catch {}
        return Promise.reject(refreshErr); // session really expired
      }
    }

    // Just bubble the original error (e.g., Invalid email or password.)
    return Promise.reject(error);
  }
);

export default httpClient;
